<template>

    <template v-if="isClientAssetManager">
        <p class="inline-message is--warning">Note that making any changes to the Asset Library may impact projects of which you may not be a member. Speak to your Client Owner for more details.</p>
        <br>
    </template>

<div class="grid">

    <app-error
        v-if="errors"
        class="is--inline col-12"
        v-bind="{ errors }" />

    <div class="app-asset-manager grid col-12">

        <div v-show="status"
            :class="['status', { 'status--show': status }]">
            <div class="status-message">{{ status }}</div>
            <div v-show="uploadPercentage"
                class="element-progress">
                <p class="element-progress__text">{{ uploadPercentage }}%</p>
                <div class="element-progress__bar">
                    <div
                        class="element-progress__bar-filled"
                        role="progressbar"
                        aria-valuenow="0"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :style="`width: ${uploadPercentage}%;`">
                    </div>
                </div>
            </div>
        </div>

        <div class="app-asset-manager__sidebar app-vertical-navigation col-12 col-sm-6 col-md-5 col-xl-3">
            <div class="app-vertical-navigation__header">
                <h2 class="app-vertical-navigation__header-title">Asset Library</h2>
            </div>
            <div class="app-vertical-navigation__menu-item is--static">
                <svg class="element-icon--upload element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-upload"></use></svg>
                <h3 class="app-vertical-navigation__menu-item-title">Upload</h3>
            </div>
            <div class="app-vertical-navigation__submenu-item is--static">
                <file-pond
                    name="asset"
                    ref="asset-upload"
                    class-name="app-asset-manager__asset-upload"
                    credits="false"
                    allow-multiple="true"
                    allow-revert="true"
                    allow-process="false"
                    allow-image-preview="false"
                    label-tap-to-undo="press to dismiss"
                    label-idle='<span class="filepond--label-action">Click to upload</span> or drag and drop <span class="filepond--filetypes"></span>'
                    :max-file-size="maxFileSize"
                    :accepted-file-types="mimeTypes"
                    :file-validate-type-detect-type="validateFileType"
                    :server="serverConfig"
                    @processfile="onFileProcess" />
            </div>
            <div class="app-vertical-navigation__menu-item is--static">
                <svg class="element-icon--search element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-search"></use></svg>
                <h3 class="app-vertical-navigation__menu-item-title">View</h3>
            </div>
            <div class="app-vertical-navigation__submenu-item is--static">
                <div class="app-search">
                    <div class="input text">
                        <input v-model="filter"
                            type="search"
                            placeholder="Search by name...">
                    </div>
                </div>
            </div>
            <div v-if="types.length && !mode" class="app-vertical-navigation__submenu-item is--static">
                <div class="app-asset-manager__media-types">
                    <label v-for="type in types"
                        :key="`filetype-${type}`"
                        :title="type"
                        class="app-asset-manager__media-type">
                        <div class="input checkbox">
                            <input v-model="selectedTypes"
                                type="checkbox"
                                :value="type"
                                autocomplete="off"
                                :disabled="onlySelectedFilter(type)">
                        </div>
                        <span class="app-asset-manager__media-type-info">
                            <svg :class="['element-icon', 'element-icon--asset-' + type]" width="1em" height="1em"><use :href="['/img/icons.svg#icon-asset-' + type]"></use></svg>
                            {{ type }}
                        </span>
                    </label>
                </div>
            </div>
            <div v-if="!mode && showAssignmentFilters" class="app-vertical-navigation__submenu-item is--static">
                <div class="element-button-group">
                    <button v-for="type in assignmentTypes"
                        :key="`assignment-${type.label}`"
                        type="button"
                        :class="['element-button-group__button', { 'is--active' : selectedAssignment === type.value }]"
                        @click="selectedAssignment = type.value">
                        {{ type.label }}
                    </button>
                </div>
            </div>
            <asset-manager-folder
                class="root-tree"
                :folder="root"
                :collapsed="false">
            </asset-manager-folder>
            <div v-if="!loaded">
                Loading, please wait...
            </div>
            <p v-else-if="!filteredFiles.length"
                class="app-asset-manager__empty element-empty-instructions">
                No assets found.
            </p>
        </div>

        <div class="app-asset-manager__preview col-12 col-sm-6 col-md-7 col-xl-9">
            <div class="app-asset-manager__preview-header">
                <h2 class="app-asset-manager__preview-title">Preview {{ filename }}</h2>
                <div class="app-asset-manager__preview-header-actions-wrapper" v-if="file">
                    <div v-if="selectMode">
                        <button class="element-button--primary element-button" @click="selectFile">Select</button>
                    </div>
                    <div v-else-if="metadata.isClientAsset && !isClientAssetManager">
                        <span class="element-badge is--info">Client Asset</span>
                        <span data-bs-toggle="dropdown" >
                            <svg class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                            <div class="app-tooltip dropdown-menu">
                                <h3 class="app-tooltip__title">Client Asset</h3>
                                <div class="app-tooltip__body">
                                    <p>This asset was uploaded at the Client level and cannot be updated at the Project level.</p>
                                </div>
                            </div>
                        </span>
                    </div>
                    <div class="app-asset-manager__preview-header-actions" v-else>

                        <div v-if="showTranslatableCheckbox && !isLiteMode" class="input checkbox">
                            <input
                                type="checkbox"
                                :id="`is-translatable-${file.id}`"
                                :disabled="filesLoading[file.id]"
                                v-model="file.is_translatable"
                                :true-value="1"
                                :false-value="0"
                                @change="toggleTranslatable($event)">
                                <label :for="`is-translatable-${file.id}`">Has Translation</label>
                        </div>                       

                        <!-- Consolidated Captions Dropdown -->
                        <b-dropdown text="Captions" class="element-button--secondary-dropdown-captions element-button--secondary-dropdown" v-if="metadata.supportsCaptions && !selectMode">
                            <b-dropdown-item>
                                <file-uploader
                                    :url="`/media/${file.id}/edit`"
                                    name="captions"
                                    :button-label="metadata.captions ? 'Replace Captions' : 'Upload Captions'"
                                    :valid-extensions="['.vtt']"
                                    @start="onFileUploadStart"
                                    @progress="onFileUploadProgress"
                                    @success="onFileUploadSuccess"
                                    @fail="onFileUploadFail">
                                </file-uploader>
                            </b-dropdown-item>
                            <template v-if="metadata.captions_url">
                                <b-dropdown-item>
                                    <a
                                        title="Download Captions"
                                        :href="`/media/${file.id}/captions/download`"
                                        target="_blank">
                                        <svg class="element-icon--download element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-download"></use></svg>
                                        Download Captions
                                    </a>
                                </b-dropdown-item>
                                <b-dropdown-item v-if="metadata.captions_url">
                                    <button
                                        title="Delete Captions"
                                        @click="deleteCaptions(`${file.id}`)"
                                        class="element-button is--alert">
                                        <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                        Delete Captions
                                    </button>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>

                        <file-uploader
                            :url="`/media/${file.id}/edit`"
                            button-label="Replace Asset"
                            button-class="element-button--primary element-button"
                            :valid-extensions="getExtensions(file.type)"
                            @start="onFileUploadStart"
                            @progress="onFileUploadProgress"
                            @success="onFileUploadSuccess"
                            @fail="onFileUploadFail">
                        </file-uploader>
                        <button v-if="!metadata.isClientAsset || (metadata.isClientAsset && isClientAssetManager)"
                            class="element-button--icon is--alert element-button"
                            title="Delete Asset"
                            @click="deleteFile(file)">
                            <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="file"
                class="app-asset-manager__preview-body"
                :key="`${file.id}-${file.timestamp}`">
                <div class="app-asset-manager__preview-file" v-if="!selectMode">
                    <div class="input text">
                        <label>Filename</label>
                        <input
                            type="text"
                            spellcheck="false"
                            :disabled="metadata.isClientAsset || filesLoading[file.id]"
                            :value="filename"
                            @change="updateFilename($event)">
                    </div>
                    <span class="app-asset-manager__preview-file-extension">.{{ extension }}</span>
                </div>

                <div class="app-asset-manager__preview-asset">
                    <img v-if="file.type === 'image'"
                        :title="file.url"
                        :src="fileUrl">
                    <audio v-else-if="file.type === 'audio'"
                        controls
                        :title="file.url"
                        :src="fileUrl">
                    </audio>
                    <video v-else-if="file.type === 'video'"
                        controls
                        :title="file.url"
                        :src="fileUrl">
                        <track v-if="metadata.captions"
                            default
                            srclang="en"
                            :src="`/assets/${metadata.captions}`"
                        />
                    </video>
                    <div v-else
                        class="app-asset-manager__preview-asset-unavailable">
                        <p>Preview not available for {{ file.type }} files.</p>
                        <a
                            :title="file.url"
                            :href="file.url"
                            target="_blank"
                            class="element-button--secondary element-button">
                            Open File
                        </a>
                    </div>
                 </div>

                <div class="app-asset-manager__preview-metadata">
                    <h3 class="page-main__section-subtitle">Metadata</h3>
                    <p>
                        <strong>Asset Type</strong>
                        <app-loader-fill v-if="!metadata.mimeType" />
                        <span v-else>{{ metadata.mimeType }}</span>
                    </p>
                    <p>
                        <strong>Last Modified</strong>
                        <app-loader-fill v-if="!metadata.updatedAtFormatted" />
                        <span v-else>{{ metadata.updatedAtFormatted }}</span>
                    </p>
                    <p v-if="metadata.uploadUser">
                        <strong>Modified By</strong>
                        {{ metadata.uploadUser }}
                    </p>
                    <p>
                        <strong>File Size</strong>
                        <app-loader-fill v-if="!metadata.size" />
                        <span v-else>{{ metadata.size }}</span>
                        <strong v-show="metadata.optimized" class="element-badge is--finished">Optimized</strong>
                    </p>
                    <p v-if="metadata.hasDimensions">
                        <strong>File Dimensions</strong>
                        <app-loader-fill v-if="!metadata.dimensions" />
                        <span v-else>{{ metadata.dimensions.width }} x {{ metadata.dimensions.height }} px</span>
                    </p>
                    <p v-if="metadata.hasDuration">
                        <strong>File Duration</strong>
                        <app-loader-fill v-if="!metadata.duration" />
                        <span v-else>{{ metadata.durationFormatted }}</span>
                    </p>
                    <p v-if="metadata.isClientAsset">
                        <strong>Client Asset</strong>
                        <app-loader-fill v-if="!metadata.isClientAsset" />
                        <span v-else>Yes</span>
                    </p>
                </div>
                

                <div class="app-asset-manager__assignments" v-if="!selectMode && showAssignmentFilters"> 
                    <table>
                        <thead>
                            <tr>
                                <th width="40%">Assigned Objects</th>
                                <th>Assigned Screens</th>
                            </tr>
                        </thead>
                        <tbody v-if="!metadata.assigned && !metadata.usage" class="app-loader__wrapper">
                            <app-loader />
                        </tbody>
                        <tbody v-else>
                            <tr v-for="(source, tag) in metadata.usage" :key="tag">
                                <td>
                                    <a :href="source.url" class="element-link">{{ source.name }}</a>
                                </td>
                                <td>
                                    <a v-for="(object) in source.objects" :key="object.id" :href="object.url" class="element-link">{{ object.name }}</a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-if="!metadata.assigned" class="empty-table">This asset hasn't been added to any screens.</p>
                </div>
            </div>
            <div v-else class="app-asset-manager__preview-body-empty element-empty-instructions">
                <img class="app-asset-manager__preview-body-empty-img" src="/img/undraw_select_option_re_u4qn.svg" />
                <p>Select a file to preview, or upload a new one.</p>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import FileUploader from './FileUploader.vue'
import AssetManagerFolder from './AssetManagerFolder.vue'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import api from '../api/assets'
import { classifyUploadError, parseUploadResponseError } from '../helpers/uploadErrorClassifier'

// styles
import 'filepond/dist/filepond.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// Create FilePond component
const FilePond = vueFilePond(
    FilePondPluginFileValidateType,
    FilePondPluginFileValidateSize,
    FilePondPluginImagePreview
)

export default {
    components: {
        AssetManagerFolder,
        FileUploader,
        FilePond
    },
    props: {
        files: {
            type: Array,
            default: () => []
        },
        maxFileSize: {
            type: Number,
            default: (50 * 1024 * 1024) // 50MB
        },
        source: {
            type: String
        },
        destination: {
            type: String
        },
        selectMode: {
            type: Boolean,
            default: false
        },
        validExtensions: {
            type: Object,
            default: () => { }
        },
        validMimeTypes: {
            type: Object,
            default: () => { }
        },
        showAssignmentFilters: {
            type: Boolean,
            default: true
        },
        showTranslatableCheckbox: {
            type: Boolean,
            default: true
        },
        isLiteMode: {
            type: [Boolean, Number],
            default: false
        },
        isClientAssetManager: {
            type: Boolean,
            default: false
        },
        autoLoad: {
            type: Boolean,
            default: true
        }
    },
    emits: ['fileSelected'],
    data () {
        return {
            selectedTypes: _.keys(this.validExtensions),
            assignmentTypes: [{
                label: 'All',
                value: null
            }, {
                label: 'Assigned',
                value: true
            }, {
                label: 'Unassigned',
                value: false
            }],
            selectedAssignment: null,
            filter: '',
            status: null,
            errors: false,
            errorMessage: null,
            uploadPercentage: 0
        }
    },
    computed: {
        ...mapState('assets', {
            ready: state => state.ready,
            loaded: state => state.loaded,
            options: state => state.options,
            selectedId: state => state.selectedId,
            filesLoading: state => state.filesLoading
        }),
        ...mapGetters('assets', {
            allFiles: 'fileSet',
            file: 'selectedFile',
            metadata: 'selectedMetadata'
        }),
        serverConfig () {
            return {
                process: {
                    url: this.uploadUrl,
                    onerror: this.onServerResponseError,
                },
                fetch: null,
                revert: null,
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.$store.state.csrfToken
                }
            }
        },
        uploadUrl () {
            return this.destination || this.source
        },
        mode () {
            return _.get(this.options, 'assetType')
        },
        filename () {
            return this.file?.name.replace(/\.[^/.]+$/, '')
        },
        fileUrl () {
            const timestamp = _.get(this.file, 'timestamp', 0)
            return `${this.file?.url}?t=${timestamp}`
        },
        extension () {
            return /(?:\.([^.]+))?$/.exec(this.file?.name)[1]
        },
        extensions () {
            if (this.mode) {
                return this.getExtensions(this.mode)
            }
    
            const list = _.reduce(this.validExtensions, (result, extensions) => {
                const formattedExt = _.map(extensions, (ext) => `.${ext}`)
                return result.concat(formattedExt)
            }, [])

            return _.uniq(list)
        },
        mimeTypes () {
            if (this.mode) {
                let _result = [];
                if (_.isArray(this.mode))
                {
                    const _all = this.validMimeTypes;
                    _(this.mode).each(function(_type) {
                        _result = _.union(_result, _.get(_all, _type, []))
                    });
                    return _result
                }
                else
                {
                    return _.get(this.validMimeTypes, this.mode, [])
                }
            }
    
            return _.reduce(this.validMimeTypes, (result, types) => result.concat(types), [])
        },
        filteredFiles () {
            let filtered = this.allFiles

            const typeFilters = !this.mode && this.selectedTypes.length ? this.selectedTypes : this.types

            // filter by file types
            filtered = _.filter(filtered, ({ type }) => {
                return typeFilters.indexOf(type) >= 0
            })

            // filter by assignment
            if (this.selectedAssignment !== null) {
                filtered = _.filter(filtered, ({ data_objects = [] }) => {
                    return _.xor([!!data_objects.length, this.selectedAssignment]).length === 1
                })
            }

            if (this.filter.length) {
                filtered = _.filter(filtered, ({ name }) => {
                    return name.toLowerCase().indexOf(this.filter.toLowerCase()) >= 0 
                })
            }

            return filtered
        },
        types () {
            const types = this.mode ? (_.isArray(this.mode) ? this.mode : [this.mode]) : _.keys(this.validExtensions)
            return types.sort()
        },
        root () {
            return { files: this.filteredFiles }
        }
    },
    methods: {
        handleFilePondInit () {
            // console.log('FilePond has initialized')
        },
        validateFileType: (source, type) => new Promise((resolve) => {
            if (source.name.endsWith('woff')) {
                return resolve('font/woff')
            } else if (source.name.endsWith('woff2')) {
                return resolve('font/woff2')
            }

            resolve(type)
        }),
        selectFile () {
            this.$emit('file-selected', this.file)
        },
        getIcon (type) {
            let icon = type
            switch (type) {
                case 'image':
                case 'audio':
                case 'video':
                case 'font':
                    break
                default:
                    icon = 'document'
                    break
            }

            return icon
        },
        getExtensions (type) {
            let extensions = [];
            if (_.isArray(type))
            {
                const _all = this.validExtensions;
                _(type).each(function(_type) {
                    extensions = _.union(extensions, _.get(_all, _type))
                });
            }
            else
            {
                extensions = _.get(this.validExtensions, type)
            }

            return _.map(extensions, (ext) => `.${ext}`)
        },
        onlySelectedFilter (type) {
            return this.selectedTypes.length === 1 && this.selectedTypes.indexOf(type) >= 0
        },
        setStatus (message, progress = 0) {
            this.status = message
            this.uploadPercentage = progress
        },
        onFileUploadStart () {
            // this.setStatus('Starting file upload...')
            this.errors = false
        },
        onFileUploadProgress ({ loaded, total }) {
            // this.setStatus('Uploading...', Math.round(loaded / total * 100))
        },
        // onerror in serverConfig: only returns the label string for FilePond's inline file-item UI
        onServerResponseError(response) {
            return parseUploadResponseError(response)
        },
        onFileUploadFail (errors) {
            console.log(errors)
            this.errors = errors
        },
        onFileProcess (error, data) {
            // if error occurs, display parsed response message or fallback to generic message per status type
            if (error) {
                this.onFileUploadFail({ message: error?.body || classifyUploadError(error?.code) })
                return
            }

            try {
                const response = JSON.parse(data.serverId)
                const file = _.get(response, 'data')
                this.onFileUploadSuccess(file)
            } catch (err) {
                console.error('Error parsing success response:', err)
                this.errors = { message: ['Upload failed. Please try again.'] }
            }
        },
        async toggleTranslatable (e, file) {
            await this.$store.dispatch('assets/updateFile', { id: this.selectedId, is_translatable: this.file.is_translatable })
        },
        async onFileUploadSuccess (file) {
            // select file
            if (file) {
                this.$store.commit('assets/addFile', file)
                await this.$store.dispatch('assets/selectFile', file.id)
            }

            this.setStatus(null)
        },
        async updateFilename (e) {
            let newName = e.target.textContent || e.target.innerText || e.target.value
            if (!newName.length) {
                newName = this.file.name
            }
            const name = `${newName}.${this.extension}`
            if (name !== this.file.name) {
                this.file.name = name
                try {
                    await this.$store.dispatch('assets/updateFile', { id: this.selectedId, name })
                    this.errors = false
                } catch (err) {
                    const errors = _.get(err, 'response.data.errors')
                    if (errors)
                        this.errors = errors
                    else
                        this.errors = { message: ['An error occurred while updating the filename.'] }
                }
            }
        },
        async deleteFile (file) {
            if (!confirm(`Are you sure you want to delete [${file.name}]?`)) {
                return
            }
            
            // this.setStatus(`Deleting [${file.name}]`)

            const result = await this.$store.dispatch('assets/deleteFile', file.id)
            if (result) {
                this.$store.commit('assets/setSelectedId', null)
            }

            this.setStatus(null)
        },
        async deleteCaptions(mediaId) {
            if (!confirm(`Are you sure you want to delete captions?`)) {
                return
            }
            try {
                const response = await api.deleteCaptions(mediaId);

                if (response)
                    this.onFileUploadSuccess(response);

            } catch (e) {
                alert('Could not delete captions.')
            }
        },
    },
    async created () {
        this.$store.commit('assets/setSource', this.source)
        this.$store.commit('assets/setFiles', this.files)

        if (this.autoLoad) {
            if (!this.files.length) {
                await this.$store.dispatch('assets/update')
            } else {
                this.$store.commit('assets/setLoaded', true)
            }
        }

        // check for initial selection (if not select mode)
        if (!this.selectMode) {
            const hash = window.location.hash.substr(1)
            if (hash) {
                this.$store.dispatch('assets/selectFile', parseInt(hash, 10))
            }
        }

        this.$store.commit('assets/setReady', true)
    }
}
</script>
